import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import API from '../utils/api';
import {
  INIT_CUSTOMERS, INIT_VESSELS, INIT_SUPPLIERS, INIT_LOCATIONS,
  INIT_FREIGHT, INIT_COUNTRIES, INIT_STOCK, INIT_DNS
} from '../data/mockData';

// Where to fetch uploaded photos from — same host the API itself is on.
// Derived from API's own baseURL instead of a hardcoded host, so this stays correct
// on staging (stage-api.allinlogwms.com) as well as production (different host).
const PHOTO_HOST = API.defaults.baseURL.replace(/\/api\/?$/, '');

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  // Check if user is already logged in (Persistence)
  const initialUser = JSON.parse(localStorage.getItem('userInfo'));

  const [role, setRole] = useState(initialUser?.role || 'admin');
  const [customers, setCustomers] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [currentPage, setCurrentPage] = useState(localStorage.getItem('currPage') || (initialUser?.role === 'customer' ? 'my-stock' : 'dashboard'));
  const [pageData, setPageData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [freightTypes, setFreight] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [stock, setStock] = useState([]);
  const [dns, setDns] = useState([]);
  const [users, setAdminUsers] = useState([]);

  /* ---- Individual fetchers — each loads/maps/sets exactly one piece of
     shared state. CRUD actions call only the specific fetcher(s) for what
     they actually changed, instead of re-pulling everything. ---- */
  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/customers');
      setCustomers(data);
    } catch (err) { console.error('Error fetching customers:', err); }
  }, []);

  const fetchVessels = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/vessels');
      setVessels(data.map(v => ({ ...v, customerIds: v.Customers?.map(c => c.id) || [] })));
    } catch (err) { console.error('Error fetching vessels:', err); }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/suppliers');
      setSuppliers(data.map(s => {
        const ft = s.freightTypes || s.FreightTypes || [];
        return { ...s, freightTypeIds: ft.map(f => f.id) || [], freightTypes: ft.map(f => f.name) || [] };
      }));
    } catch (err) { console.error('Error fetching suppliers:', err); }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/locations');
      setLocations(data);
    } catch (err) { console.error('Error fetching locations:', err); }
  }, []);

  const fetchFreightTypes = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/freight-types');
      setFreight(data);
    } catch (err) { console.error('Error fetching freight types:', err); }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/countries');
      setCountries(data);
    } catch (err) { console.error('Error fetching countries:', err); }
  }, []);

  const fetchTrucks = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/trucks');
      setTrucks(data);
    } catch (err) { console.error('Error fetching trucks:', err); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/users');
      setAdminUsers(data);
    } catch (err) { console.error('Error fetching users:', err); }
  }, []);

  const fetchStock = useCallback(async () => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    try {
      const { data } = await API.get('/warehouse/stock');
      // Transformation: Backend snake_case -> Frontend properties
      const mappedStock = data.map(s => ({
        ...s,
        po: s.po_numbers,
        qty: s.quantity,
        type: s.process_type === 'offland' ? 'Offland' : 'Incoming', // UI expects capitalized
        date: s.created_at ? new Date(s.created_at).toLocaleDateString() : '—',
        time: s.created_at ? new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
        customer: s.Customer?.company || s.Customer?.company_name || '—',
        vessel: s.Vessel?.name || '—',
        supplier: s.Supplier?.name || '—',
        location: s.Location?.code || '',
        frtType: s.FreightType?.name || '—',
        awb: s.awb_bl_number || '',
        port: s.port || '',
        description: s.description || '',
        remarks: s.remarks || '',
        pkgType: s.pkg_type || 'PKG',
        unit: s.packaging_unit || 'KGS',
        dimension: s.dimensions || '',
        // Comma-separated, same order as "dimension"; used by Label Printing.
        itemWeights: s.item_weights || '',
        photos: (s.Photos || []).map(p => ({
          ...p,
          photo_url: p.photo_url.startsWith('http') ? p.photo_url :
                    (p.photo_url.startsWith('/uploads') ? `${PHOTO_HOST}${p.photo_url}` : `${PHOTO_HOST}/uploads/${p.photo_url}`),
          isDoc: p.photo_url.toLowerCase().endsWith('.pdf') || p.photo_url.toLowerCase().endsWith('.doc') || p.photo_url.toLowerCase().endsWith('.docx')
        })),
        currentStep: s.current_step || 1,
        // --- Delivery info (auto-filled from the DN this stock was linked to) ---
        outDate: s.DeliveryNotes?.[0]?.delivery_date || (s.DeliveryNotes?.[0]?.created_at ? String(s.DeliveryNotes[0].created_at).split('T')[0] : ''),
        dnNumber: s.DeliveryNotes?.[0]?.dn_number || '',
        deliveredTo: s.DeliveryNotes?.[0]?.delivered_to || ''
      })).sort((a, b) => b.id - a.id);

      // --- Role-Based Data Isolation (Customer Privacy) ---
      let finalStock = mappedStock;
      if (currentUser?.role === 'customer' && currentUser?.customer_id) {
        const myId = +currentUser.customer_id;
        finalStock = mappedStock.filter(s => +s.customer_id === myId);
      }
      setStock(finalStock);
    } catch (err) { console.error('Error fetching stock:', err); }
  }, []);

  const fetchDNs = useCallback(async () => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    try {
      const { data } = await API.get('/delivery');
      const mappedDNs = data.map(d => ({
        ...d,
        dnNumber: d.dn_number,
        totalWeight: d.manual_total_weight,
        packages: d.manual_packages,
        dimensions: d.manual_dimensions || '—',
        packageType: d.package_type,
        deliveryType: d.delivery_type,
        doc_type: d.doc_type || 'Delivery Note',
        docType: d.doc_type || 'Delivery Note',
        customer: d.StockEntries?.[0]?.Customer?.company || 'N/A',
        vessel: d.StockEntries?.[0]?.Vessel?.name || 'N/A',
        createdDate: d.created_at ? d.created_at.split('T')[0] : 'N/A',
        items: d.StockEntries?.map(s => ({
          ...s,
          po: s.po_numbers,
          qty: s.quantity,
          weight: s.weight,
          unit: s.packaging_unit || s.unit || 'KGS',
          pkgType: s.pkg_type || s.pkgType || 'PKG',
          vessel: s.Vessel?.name || 'N/A',
          supplier: s.Supplier?.name || 'N/A',
          awb: s.awb_bl_number || 'N/A',
          frtType: s.FreightType?.name || 'N/A'
        })) || []
      }));

      // --- Role-Based Data Isolation (Customer Privacy) ---
      let finalDNs = mappedDNs;
      if (currentUser?.role === 'customer' && currentUser?.customer_id) {
        const myId = +currentUser.customer_id;
        finalDNs = mappedDNs.filter(d => {
          // 1. Must have items belonging to this customer
          const items = d.items || [];
          const hasOwnItems = items.some(s => +s.customer_id === myId || +s.Customer?.id === myId);
          return hasOwnItems;
        }).map(d => ({
          ...d,
          // 2. Double-ensure only OWN items are in the items array (Filter nested items too)
          items: (d.items || []).filter(s => +s.customer_id === myId || +s.Customer?.id === myId)
        }));
      }
      setDns(finalDNs);
    } catch (err) { console.error('Error fetching DNs:', err); }
  }, []);

  // Guards against overlapping fetchAllData() calls (e.g. login + a
  // near-simultaneous manual refresh both firing their own full burst).
  // If a fetch is already running, queue a single follow-up run right after
  // it finishes instead of piling another burst on top.
  const fetchInFlight = useRef(false);
  const fetchQueued = useRef(false);

  // Full refresh — every piece of shared data at once. Used once at login
  // and by a couple of manual "refresh everything" spots; NOT called on
  // every navigation anymore (that was the source of ~10 parallel requests
  // firing on every single tab click). CRUD actions below instead call only
  // the specific fetcher(s) for what they actually changed.
  const fetchAllData = useCallback(async () => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    if (!currentUser?.token) return;
    if (fetchInFlight.current) {
      fetchQueued.current = true;
      return;
    }
    fetchInFlight.current = true;
    try {
      const tasks = [
        fetchCustomers(), fetchVessels(), fetchSuppliers(), fetchLocations(),
        fetchFreightTypes(), fetchCountries(), fetchTrucks(), fetchUsers()
      ];
      // Fleet Controller has no access to warehouse stock/delivery data — don't even fetch it
      if (currentUser.role !== 'fleet_controller') {
        tasks.push(fetchStock(), fetchDNs());
      }
      await Promise.all(tasks);
    } finally {
      fetchInFlight.current = false;
      if (fetchQueued.current) {
        fetchQueued.current = false;
        fetchAllData();
      }
    }
  }, [fetchCustomers, fetchVessels, fetchSuppliers, fetchLocations, fetchFreightTypes, fetchCountries, fetchTrucks, fetchStock, fetchDNs, fetchUsers]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  const [toast, setToast] = useState({ show: false, msg: '', type: '' });
  const [modal, setModal] = useState({ open: false, type: null, data: null });
  const [loggedUser, setLoggedUser] = useState(initialUser ? {
    ...initialUser,
    phone: initialUser.phone || '',
    bio: `${initialUser.role} account`,
    photo: null
  } : null);

  /* ---- Toast ---- */
  const showToast = useCallback((msg, type = '') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: '', type: '' }), 3000);
  }, []);

  /* ---- Modal ---- */
  const openModal = useCallback((type, data = null) => setModal({ open: true, type, data }), []);
  const closeModal = useCallback(() => setModal({ open: false, type: null, data: null }), []);

  /* ---- Role & User ---- */
  const cycleRole = useCallback(() => {
    const list = ['admin', 'warehouse', 'customer'];
    setRole(r => {
      const next = list[(list.indexOf(r) + 1) % list.length];
      showToast(`Switched to ${next === 'admin' ? 'Administrator' : next === 'warehouse' ? 'Warehouse Staff' : 'Customer'}`, '');

      // Reset mock user profile on role switch
      setLoggedUser({
        name: next === 'admin' ? 'Ahmad Malik' : next === 'warehouse' ? 'Budi Wibowo' : 'Ahmad Rizki',
        email: `${next}@allin-logistics.com`,
        phone: '+971 50 123 4567',
        bio: `${next.charAt(0).toUpperCase() + next.slice(1)} account for testing.`,
        photo: null
      });

      return next;
    });
  }, [showToast]);

  const updateUser = useCallback((updates, silent = true) => {
    setLoggedUser(prev => ({ ...prev, ...updates }));
    if (silent === false) showToast('Profile updated successfully!', 'success');
  }, [showToast]);

  const navigate = useCallback((page, data = null) => {
    setCurrentPage(page);
    setPageData(data);
    localStorage.setItem('currPage', page);
    // No fetch here anymore — everything loads once at login, and each CRUD
    // action below refreshes only the specific data it actually changed.
  }, []);

  /* ---- Stock CRUD (Connected to API) ---- */
  const addStock = useCallback(async (formData) => {
    try {
      const { data } = await API.post('/warehouse/intake', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update local state with the new entry (re-fetching or manual update)
      fetchStock();
      showToast('✓ Entry submitted for admin approval!', 'success');
      return data.id;
    } catch (err) {
      console.error("Stock Intake Error:", err);
      showToast('Failed to submit entry. Check your connection.', 'error');
      throw err;
    }
  }, [showToast, fetchStock]);

  const approveStock = useCallback(async (id) => {
    try {
      const { data } = await API.put(`/admin/approve-stock/${id}`);
      setStock(p => p.map(s => s.id === id ? { ...s, status: 'active', rejection_reason: undefined } : s));
      showToast('✓ Approved — item is now Active Stock!', 'success');
      fetchStock(); // Refresh to get updated association data if any
    } catch (err) {
      showToast(err.response?.data?.message || 'Error approving stock', 'error');
    }
  }, [showToast, fetchStock]);

  const rejectStock = useCallback(async (id, reason) => {
    try {
      const { data } = await API.put(`/admin/reject-stock/${id}`, { reason });
      setStock(p => p.map(s => s.id === id ? { ...s, status: 'rejected', rejection_reason: reason } : s));
      showToast('Item rejected and sent back to warehouse.', 'error');
      fetchStock();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error rejecting stock', 'error');
    }
  }, [showToast, fetchStock]);

  const updateStock = useCallback(async (id, updates) => {
    try {
      await API.put(`/admin/update-stock/${id}`, updates);
      fetchStock();
      showToast('✓ Changes saved!', 'success');
    } catch (err) { showToast('Error updating stock', 'error'); }
  }, [showToast, fetchStock]);

  const deleteStock = useCallback(async (id) => {
    try {
      await API.delete(`/admin/delete-stock/${id}`);
      setStock(p => p.filter(s => s.id !== id));
      showToast('✓ Entry deleted permanently.', 'success');
      fetchStock();
    } catch (err) { showToast(err.response?.data?.message || 'Error deleting item', 'error'); }
  }, [showToast, fetchStock]);

  const completeStock = useCallback(async (id, formData) => {
    try {
      await API.put(`/admin/complete-stock/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStock(p => p.map(s => s.id === id ? { ...s, status: 'completed' } : s));
      showToast('✓ Item marked Completed!', 'success');
      fetchStock();
    } catch (err) { showToast(err.response?.data?.message || 'Error completing item', 'error'); }
  }, [showToast, fetchStock]);

  /* ---- Draft CRUD (API-based) ---- */
  const saveDraft = useCallback(async (formData) => {
    try {
      const draftPayload = {
        process_type: formData.type ? (formData.type.toLowerCase() === 'incoming' ? 'incoming' : 'offland') : null,
        customer_id: formData.customer || null,
        vessel_id: formData.vessel || null,
        supplier_id: formData.supplier || null,
        po_numbers: formData.po || null,
        awb_bl_number: formData.awb || null,
        quantity: formData.qty || null,
        weight: formData.weight || null,
        dimensions: formData.dimList ? formData.dimList.map(d => `${d.length}X${d.width}X${d.height}`).join(', ') : null,
        item_weights: formData.dimList ? formData.dimList.map(d => d.weight || '').join(', ') : null,
        freight_type_id: formData.frtType || null,
        origin_country_id: formData.origin || null,
        warehouse_location_id: formData.location || null,
        port: formData.port || null,
        description: formData.description || null,
        remarks: formData.remarks || null,
        pkg_type: formData.pkgType || 'PKG',
        packaging_unit: formData.unit || 'KGS',
        current_step: formData.current_step || 1
      };

      let res;
      if (formData.draftId) {
        res = await API.put(`/warehouse/draft/${formData.draftId}`, draftPayload);
      } else {
        res = await API.post('/warehouse/draft', draftPayload);
      }
      await fetchStock(); // Refresh stock list to show the new/updated draft
      return res.data.id;
    } catch (err) {
      showToast('Error saving draft', 'error');
      throw err;
    }
  }, [showToast, fetchStock]);

  const deleteDraft = useCallback(async (draftId) => {
    try {
      await API.delete(`/warehouse/draft/${draftId}`);
      setStock(p => p.filter(s => s.id !== draftId));
      showToast('Draft deleted.', '');
    } catch (err) { 
      const msg = err.response?.data?.message || 'Error saving draft';
      showToast(msg, 'error'); 
    }
  }, [showToast]);

  /* ---- DN ---- */
  const genDNNumber = useCallback(() => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const seq = String(dns.length + 1).padStart(4, '0');
    return `ALN${yy}${mm}${seq}`;
  }, [dns]);

  /* ---- DN (Connected to API) ---- */
  const addDN = useCallback(async (dnData) => {
    try {
      // Pass the standardized data directly
      const { data } = await API.post('/delivery', dnData);
      showToast(`✓ DN ${data.dn_number} created!`, 'success');
      fetchDNs(); fetchStock();
      return data.dn_number;
    } catch (err) {
      showToast(err.response?.data?.message || 'Error creating Delivery Note', 'error');
    }
  }, [showToast, fetchDNs, fetchStock]);

  const dispatchDN = useCallback(async (id) => {
    try {
      await API.put(`/delivery/${id}/dispatch`);
      // Optimistic local update so the status badge flips instantly — full refresh follows in background
      setDns(p => p.map(d => d.id === id ? { ...d, status: 'dispatched' } : d));
      showToast('✓ DN Dispatched!', 'success');
      fetchDNs(); fetchStock();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error dispatching DN', 'error');
    }
  }, [showToast, fetchDNs, fetchStock]);

  const updateDN = useCallback(async (id, updates) => {
    try {
      await API.put(`/delivery/${id}`, updates);
      showToast('✓ DN updated!', 'success');
      fetchDNs(); fetchStock();
    } catch (err) { showToast('Error updating DN', 'error'); }
  }, [showToast, fetchDNs, fetchStock]);

  const completeDN = useCallback(async (id, formData) => {
    try {
      await API.put(`/delivery/${id}/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('✓ Delivery Completed!', 'success');
      fetchDNs(); fetchStock();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error completing delivery', 'error');
    }
  }, [showToast, fetchDNs, fetchStock]);

  /* ---- Masters (Connected to API) ---- */
  const addCustomer = useCallback(async (c) => {
    try {
      await API.post('/admin/customers', c);
      showToast('✓ Customer added!', 'success');
      await fetchCustomers();
    } catch (err) { throw err; }
  }, [showToast, fetchCustomers]);

  const updateCustomer = useCallback(async (id, u) => {
    try {
      await API.put(`/admin/customers/${id}`, u);
      showToast('✓ Updated!', 'success');
      await fetchCustomers();
    } catch (err) { throw err; }
  }, [showToast, fetchCustomers]);

  const deleteCustomer = useCallback(async (id) => {
    try {
      await API.delete(`/admin/customers/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchCustomers();
    } catch (err) { throw err; }
  }, [showToast, fetchCustomers]);

  const addVessel = useCallback(async (v) => {
    try {
      await API.post('/admin/vessels', v);
      showToast('✓ Vessel added!', 'success');
      await fetchVessels();
    } catch (err) { throw err; }
  }, [showToast, fetchVessels]);

  const updateVessel = useCallback(async (id, u) => {
    try {
      await API.put(`/admin/vessels/${id}`, u);
      showToast('✓ Updated!', 'success');
      await fetchVessels();
    } catch (err) { throw err; }
  }, [showToast, fetchVessels]);

  const deleteVessel = useCallback(async (id) => {
    try {
      await API.delete(`/admin/vessels/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchVessels();
    } catch (err) { throw err; }
  }, [showToast, fetchVessels]);

  const addSupplier = useCallback(async (s) => {
    try {
      await API.post('/admin/suppliers', s);
      showToast('✓ Supplier added!', 'success');
      await fetchSuppliers();
    } catch (err) { throw err; }
  }, [showToast, fetchSuppliers]);

  const updateSupplier = useCallback(async (id, u) => {
    try {
      await API.put(`/admin/suppliers/${id}`, u);
      showToast('✓ Updated!', 'success');
      await fetchSuppliers();
    } catch (err) { throw err; }
  }, [showToast, fetchSuppliers]);

  const deleteSupplier = useCallback(async (id) => {
    try {
      await API.delete(`/admin/suppliers/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchSuppliers();
    } catch (err) { throw err; }
  }, [showToast, fetchSuppliers]);

  const addLocation = useCallback(async (l) => {
    try {
      await API.post('/admin/locations', l);
      showToast('✓ Location added!', 'success');
      await fetchLocations();
    } catch (err) { showToast('Error adding location', 'error'); }
  }, [showToast, fetchLocations]);

  const deleteLocation = useCallback(async (id) => {
    try {
      await API.delete(`/admin/locations/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchLocations();
    } catch (err) { throw err; }
  }, [showToast, fetchLocations]);

  const updateLocation = useCallback(async (id, l) => {
    try {
      await API.put(`/admin/locations/${id}`, l);
      showToast('✓ Updated!', 'success');
      await fetchLocations();
    } catch (err) { showToast('Error updating location', 'error'); }
  }, [showToast, fetchLocations]);

  const addFreight = useCallback(async (f) => {
    try {
      await API.post('/admin/freight-types', f);
      showToast('✓ Added!', 'success');
      await fetchFreightTypes();
    } catch (err) { showToast('Error adding freight type', 'error'); }
  }, [showToast, fetchFreightTypes]);

  const deleteFreight = useCallback(async (id) => {
    try {
      await API.delete(`/admin/freight-types/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchFreightTypes();
    } catch (err) { throw err; }
  }, [showToast, fetchFreightTypes]);

  const updateFreight = useCallback(async (id, f) => {
    try {
      await API.put(`/admin/freight-types/${id}`, f);
      showToast('✓ Updated!', 'success');
      await fetchFreightTypes();
    } catch (err) { showToast('Error updating freight type', 'error'); }
  }, [showToast, fetchFreightTypes]);

  const addTruck = useCallback(async (t) => {
    try {
      await API.post('/admin/trucks', t);
      showToast('✓ Truck added!', 'success');
      await fetchTrucks();
    } catch (err) { showToast('Error adding truck', 'error'); }
  }, [showToast, fetchTrucks]);

  const deleteTruck = useCallback(async (id) => {
    try {
      await API.delete(`/admin/trucks/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchTrucks();
    } catch (err) { throw err; }
  }, [showToast, fetchTrucks]);

  const updateTruck = useCallback(async (id, t) => {
    try {
      await API.put(`/admin/trucks/${id}`, t);
      showToast('✓ Updated!', 'success');
      await fetchTrucks();
    } catch (err) { showToast('Error updating truck', 'error'); }
  }, [showToast, fetchTrucks]);

  const addCountry = useCallback(async (c) => {
    try {
      await API.post('/admin/countries', c);
      showToast('✓ Added!', 'success');
      await fetchCountries();
    } catch (err) { showToast('Error adding country', 'error'); }
  }, [showToast, fetchCountries]);

  const deleteCountry = useCallback(async (id) => {
    try {
      await API.delete(`/admin/countries/${id}`);
      showToast('✓ Deleted!', 'success');
      await fetchCountries();
    } catch (err) { throw err; }
  }, [showToast, fetchCountries]);

  const updateCountry = useCallback(async (id, c) => {
    try {
      await API.put(`/admin/countries/${id}`, c);
      showToast('✓ Updated!', 'success');
      await fetchCountries();
    } catch (err) { showToast('Error updating country', 'error'); }
  }, [showToast, fetchCountries]);

  const updateAdminUser = useCallback(async (id, u) => {
    try {
      await API.put(`/admin/users/${id}`, u);
      showToast('✓ User Updated!', 'success');
      await fetchUsers();
    } catch (err) { showToast('Error updating user', 'error'); }
  }, [showToast, fetchUsers]);

  /* ---- Derived ---- */
  const pendingStock = stock.filter(s => s.status === 'pending');
  const activeStock = stock.filter(s => s.status === 'active');
  const rejectedStock = stock.filter(s => s.status === 'rejected');
  const completedStock = stock.filter(s => s.status === 'completed');

  /* Computed counts from real stock data */
  const enrichedCustomers = customers.map(c => ({
    ...c,
    activeStock: stock.filter(s => s.customer === c.company && s.status === 'active').length,
    completed: stock.filter(s => s.customer === c.company && s.status === 'completed').length,
  }));

  const enrichedVessels = vessels.map(v => ({
    ...v,
    activeStock: stock.filter(s => s.vessel === v.name && s.status === 'active').length,
    pending: stock.filter(s => s.vessel === v.name && s.status === 'pending').length,
    completed: stock.filter(s => s.vessel === v.name && s.status === 'completed').length,
  }));

  const enrichedSuppliers = suppliers.map(s => ({
    ...s,
    totalIntakes: stock.filter(st => st.supplier === s.name).length,
  }));

  return (
    <AppCtx.Provider value={{
      role, setRole, cycleRole,
      customers: enrichedCustomers, addCustomer, updateCustomer, deleteCustomer,
      vessels: enrichedVessels, addVessel, updateVessel, deleteVessel,
      suppliers: enrichedSuppliers, addSupplier, updateSupplier, deleteSupplier,
      locations, addLocation, deleteLocation, updateLocation,
      freightTypes, addFreight, deleteFreight, updateFreight,
      trucks, addTruck, deleteTruck, updateTruck,
      countries, addCountry, deleteCountry, updateCountry,
      stock, pendingStock, activeStock, rejectedStock, completedStock,
      addStock, approveStock, rejectStock, updateStock, deleteStock, completeStock,
      saveDraft, deleteDraft,
      dns, genDNNumber, addDN, updateDN, dispatchDN, completeDN,
      users, setAdminUsers, updateAdminUser,
      toast, showToast,
      modal, openModal, closeModal,
      loggedUser, updateUser, fetchAllData,
      fetchCustomers, fetchVessels, fetchSuppliers, fetchLocations,
      fetchFreightTypes, fetchCountries, fetchTrucks, fetchStock, fetchDNs, fetchUsers,
      currentPage, navigate, pageData
    }}>
      {children}
    </AppCtx.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
